import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import Url from '../../../data/url';
import { TALENT_ARR, TALENT_CATEGORIES, SELECT_STYLE } from '../../../data/define';
import ImgKidFace from '../../../assets/image/img_kidface.png';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FullHeightContainer from '../../../component/common/FullHeightContainer';
import TopHeaderWithPrev from '../../../component/common/TopHeaderWithPrev';
import IntelligenceRankLabel from '../../../component/common/IntelligenceRankLabel';
import IntelligenceExistYN from '../../../component/common/IntelligenceExistYN';
import Divider from '../../../component/common/Divider';
import { ApexOptions } from 'apexcharts';
import { useTranslation } from '../../../hook/useTranslation';

const Height = 220;
const Width = 220;

function ReportDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { results, intelligenceId, thumbnails, label } = (location.state as any) || {};
  const [selectedIntelligence, setSelectedIntelligence] = useState<number>(1);
  const [editedResult, setEditedResult] = useState<any[]>([]);
  const [series, setSeries] = useState<number[]>([0]);
  const result = results?.find((item: any) => item.intelligence === selectedIntelligence);

  const intelligenceData = thumbnails?.find(
    (thumb: any) => thumb.intelligence === selectedIntelligence
  );
  const repThumbnail = intelligenceData?.indv_thumbs[intelligenceData?.indv_thumbs.length - 1];
  const intelligenceCategories = TALENT_CATEGORIES.find(
    (intelligence) => intelligence.id === result?.intelligence
  )?.category;
  const editedCategories = result?.categories?.map((category: any) => ({
    ...category,
    label: intelligenceCategories?.find((cat: any) => cat?.category === Number(category?.category))
      ?.label,
  }));

  // 지능순위 default 설정
  useEffect(() => {
    if (results && results?.length > 0) {
      setSelectedIntelligence(intelligenceId || results[0]?.intelligence);
    }
  }, [results, intelligenceId]);

  useEffect(() => {
    if (results?.length > 0) {
      const totalResult: any[] = [];
      TALENT_ARR.forEach((talent) => {
        const existItem = results.find((item: any) => item.intelligence === talent.id);
        if (existItem) {
          totalResult.push(existItem);
        } else {
          totalResult.push({
            intelligence: talent.id,
            percentile: 0,
          });
        }
      });
      setEditedResult(totalResult);
    }
  }, [results]);

  useEffect(() => {
    if (result) {
      setSeries([result.score]);
    }
  }, [result]);

  const options: ApexOptions = {
    chart: {
      height: Height,
      type: 'radialBar',
    },
    colors: ['#74DFE0'],
    stroke: {
      lineCap: 'round',
      width: 12,
    },
    plotOptions: {
      radialBar: {
        hollow: {
          size: '50%',
        },
        track: {
          show: true,
          background: '#F8F8F8',
          strokeWidth: '100%',
        },
        dataLabels: {
          show: false,
          name: {
            show: false,
          },
          value: {
            show: false,
          },
        },
      },
    },
  };

  return (
    <FullHeightContainer>
      <TopHeaderWithPrev title="" onClickPrev={() => navigate(Url.Report)} />
      <button
        type="button"
        className="absolute top-[10px] text-[#969696] underline right-4"
        onClick={() => {
          navigate('.', {
            state: {
              results: results,
              intelligenceId: selectedIntelligence,
              thumbnails: thumbnails,
            },
            replace: true,
          });
          setTimeout(() => {
            navigate(Url.ReportDetailCommentary, {
              state: {
                intelligence: TALENT_ARR.find((talent) => talent.id === result?.intelligence),
              },
            });
          }, 100);
        }}
      >
        {t('report.detail.intelligence_detail_btn')}
      </button>
      <div className="pageInner">
        <div className="w-full flex flex-col items-center justify-start mt-[20px] pb-[30px]">
          <Select
            variant="outlined"
            value={selectedIntelligence || ''}
            onChange={(e: any) => {
              setSelectedIntelligence(e.target.value as number);
            }}
            IconComponent={KeyboardArrowDownIcon}
            size="small"
            sx={{
              ...SELECT_STYLE,
              height: '38px',
              borderRadius: '5px',
              border: '1px solid #E0E0E0',
              minWidth: '110px',
              fontWeight: 400,
            }}
          >
            {editedResult?.length >= 1 &&
              editedResult?.map((intelli, index) => (
                <MenuItem
                  key={`${intelli.intelligence}-${String(index)}`}
                  value={intelli.intelligence}
                  sx={{ fontSize: '14px' }}
                  disabled={intelli.percentile === 0}
                >
                  <span className="text-[14px] text-[#363636]">
                    {t(`talent.${TALENT_ARR.find((item) => item.id === intelli.intelligence)?.key}`)}
                  </span>
                </MenuItem>
              ))}
          </Select>
          <div className="flex items-center gap-2 justify-center text-[#1C1C1C] text-[36px] font-[700]">
            {t('report.summary.score', { score: series[0] })}
          </div>
          <div className="relative w-full flex items-center justify-center mt-[-25px]">
            <ReactApexChart
              options={options as any}
              series={series}
              type="radialBar"
              height={Height}
              width={Width}
            />
            <img
              src={ImgKidFace}
              alt="thumnail1"
              className="w-[50px] h-[50px] absolute top-[50%] left-[50%] translate-y-[-25%] translate-x-[-50%]"
            />
          </div>
          {label && <IntelligenceRankLabel label={label} />}
        </div>
        <Divider />
        <div className="grow pt-[30px] rounded-[10px] mb-[30px] bg-white flex flex-col items-start justify-center">
          <div className="flex justify-start items-center gap-2">
            <div className="font-[700] text-[20px] text-[#1C1C1C] text-left">{t('report.core.title')}</div>
          </div>
          <div className="w-full mt-[12px] flex justify-start gap-2 text-[#303133]">
            {repThumbnail?.indv_thumb_url && (
              <div
                className="rounded-[10px] shrink-0 min-w-[100px] w-[100px] min-h-[80px]"
                style={{
                  backgroundImage: repThumbnail.indv_thumb_url
                    ? `url(${repThumbnail.indv_thumb_url})`
                    : '#f8f8f8',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            )}
            <div className="bg-[#F8F8F8] p-[20px] min-h-[80px] text-left rounded-[16px] text-[#363636] text-[14px] font-[400]">
              {result?.final_summary || t('report.core.empty')}
            </div>
          </div>
        </div>
        <Divider />
        <div className="grow pt-[30px] rounded-[10px] bg-white flex flex-col items-start justify-center">
          <div className="font-[700] text-[20px] text-[#1C1C1C] text-left mb-[24px]">
            {t('report.detail.sub_characteristic_analysis')}
          </div>
          {editedCategories &&
            editedCategories?.length > 0 &&
            editedCategories?.map((category: any) => (
              <div key={category.category} className="w-full mb-[20px]">
                <div className="w-full flex items-center justify-between mb-[6px]">
                  <div className="text-[14px] font-[600] text-[#1C1C1C]">{t(`talent.${TALENT_ARR.find((i) => i.id === selectedIntelligence)?.key}.cat${category.category}`)}</div>
                  <IntelligenceExistYN rank={category.observation ? 1 : 2} />
                </div>
                <div className="bg-[#F8F8F8] w-full p-[20px] text-left rounded-[16px] text-[#363636] text-[14px] font-[400]">
                  {category.summary || t('report.detail.no_sub_analysis')}
                </div>
              </div>
            ))}
        </div>
      </div>
    </FullHeightContainer>
  );
}

export default ReportDetailPage;
